/**
 * What happens on ONE tab: every button, how often, by how many people, and which way it is going.
 *
 * ── The question this is built for ───────────────────────────────────────────────────────────
 * Two weeks after launching a tab, "which of these has the team actually adopted, and which needs
 * explaining?" The flat list this replaces could not answer it: a column of counts says Hold slots
 * was used 180 times and leaves you to guess whether that is one recruiter's habit, a spike the
 * week it shipped, or something that is still climbing. Those call for opposite responses.
 *
 * So every row carries four things that disagree with each other usefully:
 *
 *   uses     how much — the thing a bare list already said
 *   people   how far it spread. 180 uses by one person is not adoption
 *   shape    a sparkline. A launch spike and a growing habit have the same total
 *   trend    second half against first, stated only when the range can carry it
 *
 * ── Why the names are the recruiter's, not the code's ────────────────────────────────────────
 * `ta_board_ask` is precise and unreadable. The catalogue in `boardFeatures.ts` already holds what
 * each one is called in the room, and that is what leads here — with the identifier kept beside it
 * in small type, because somebody will need to grep for it.
 */
import { useQuery } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import { fetchSectionFeatures, type SectionFeature } from '../../services/usageApi';
import { TimeSeriesChart } from './TimeSeriesChart';
import { Sparkline, halfOverHalf } from './Sparkline';
import { describeEvent } from './eventDescriptions';
import { featureLabelFor } from './boardFeatures';
import { SERIES_PRIMARY, formatCompact, formatDay, formatPercent } from './chartTokens';

export function SectionFeatures({ section, days }: { section: string; days: number }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['usage-section-features', section, days],
    queryFn: () => fetchSectionFeatures(section, days),
  });

  if (isPending) return <p className="py-6 text-center text-sm text-zinc-400">Loading features…</p>;
  if (error || !data) return <p className="py-4 text-sm text-zinc-500">Could not load this tab’s features.</p>;
  if (data.features.length === 0) {
    return <p className="py-2 text-xs text-zinc-500">Nothing recorded here in this range.</p>;
  }

  const max = data.features[0]?.total || 1;
  const points = data.days.map((date, at) => ({ date, value: data.totalDaily[at] ?? 0 }));

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs font-semibold text-zinc-900">Activity on this tab</h4>
        <p className="mb-2 text-xs text-zinc-500">
          Every tracked interaction here, by Berlin day — the shape the individual features below
          add up to.
        </p>
        <TimeSeriesChart points={points} color={SERIES_PRIMARY} seriesLabel="Events on this tab" />
      </div>

      <div>
        <h4 className="text-xs font-semibold text-zinc-900">Every feature, most used first</h4>
        <p className="mb-2 max-w-[80ch] text-xs text-zinc-500">
          <strong className="font-medium text-zinc-700">People</strong> is the adoption figure —
          180 uses by one person is a habit, not a rolled-out feature. The sparkline is the same
          range as the chart above, scaled to its own peak so a quiet feature still shows a shape.
        </p>
        <ul className="divide-y divide-zinc-100">
          {data.features.map((feature) => (
            <FeatureRow key={feature.event} feature={feature} max={max} days={data.days} />
          ))}
        </ul>
      </div>
    </div>
  );
}

function FeatureRow({
  feature,
  max,
  days,
}: {
  feature: SectionFeature;
  max: number;
  days: string[];
}) {
  const label = featureLabelFor(feature.event);
  const description = describeEvent(feature.event);
  const trend = halfOverHalf(feature.daily);

  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`truncate text-xs font-medium text-zinc-900 ${label ? '' : 'font-mono'}`}
          >
            {label ?? feature.event}
          </p>
          {/* The identifier stays visible BESIDE a friendly name — somebody will need to grep for
              it. Only then: printing it twice for an event the catalogue does not name is noise,
              not provenance. */}
          {label && (
            <p className="truncate font-mono text-[11px] text-zinc-400">{feature.event}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Sparkline
            values={feature.daily}
            label={`${label ?? feature.event}: ${feature.total} uses over ${days.length} days`}
          />
          <span
            className="w-28 text-right text-xs text-zinc-500"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <span className="font-medium text-zinc-900">{formatCompact(feature.total)}</span> ·{' '}
            {feature.people} {feature.people === 1 ? 'person' : 'people'}
          </span>
        </div>
      </div>

      {/* Magnitude against the busiest feature on this tab, the same bar the page and tab rows
          use — so "clicked a lot" is readable without comparing numbers across rows. */}
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-zinc-100">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max((feature.total / max) * 100, 1)}%`,
            backgroundColor: SERIES_PRIMARY,
          }}
        />
      </div>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
        <TrendNote trend={trend} />
        {feature.lastUsed && <span>last used {formatDay(feature.lastUsed)}</span>}
        {description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help underline decoration-dotted underline-offset-2">
                what this is
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{description}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </li>
  );
}

/**
 * The second half of the range against the first.
 *
 * Deliberately plain arithmetic rather than a fitted trend: this board is read two weeks after a
 * launch, where every series is small and a regression line would dress up noise as a finding.
 * Silent when the range is too short to halve or when both halves are empty — see `halfOverHalf`.
 */
function TrendNote({ trend }: { trend: ReturnType<typeof halfOverHalf> }) {
  if (!trend) return null;
  if (!Number.isFinite(trend.change)) {
    return <span className="font-medium text-zinc-700">first used in the second half</span>;
  }
  // Under a handful of uses either side, a percentage is arithmetic rather than evidence.
  if (trend.first + trend.second < 6) {
    return <span>too few uses to call a trend</span>;
  }
  const rising = trend.change > 0.1;
  const falling = trend.change < -0.1;
  if (!rising && !falling) return <span>steady across the range</span>;
  return (
    <span className={rising ? 'font-medium text-zinc-700' : 'text-zinc-500'}>
      {rising ? '↑' : '↓'} {formatPercent(Math.abs(trend.change))} {rising ? 'up' : 'down'} on the
      first half
    </span>
  );
}
